
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { OutreachContact, OutreachInteraction } from "@/types/outreach";

export const useOutreachContacts = () => {
  return useQuery({
    queryKey: ['outreach-contacts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('outreach_contacts')
        .select('*')
        .order('last_contact_date', { ascending: false });

      if (error) throw error;
      return data as OutreachContact[];
    },
  });
};

export const useOutreachInteractions = (contactId?: string) => {
  return useQuery({
    queryKey: ['outreach-interactions', contactId],
    queryFn: async () => {
      let query = supabase
        .from('outreach_interactions')
        .select('*')
        .order('interaction_date', { ascending: false });

      if (contactId) {
        query = query.eq('outreach_contact_id', contactId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as OutreachInteraction[];
    },
    enabled: !!contactId,
  });
};

export const useCreateOutreachContact = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (contact: Omit<OutreachContact, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => {
      const { data, error } = await supabase
        .from('outreach_contacts')
        .insert({
          ...contact,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-contacts'] });
      toast({
        title: "Success",
        description: "Outreach contact created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create outreach contact",
        variant: "destructive",
      });
      console.error('Error creating outreach contact:', error);
    },
  });
};

export const useUpdateOutreachContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<OutreachContact> & { id: string }) => {
      const { data, error } = await supabase
        .from('outreach_contacts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-contacts'] });
      toast({
        title: "Success",
        description: "Outreach contact updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update outreach contact",
        variant: "destructive",
      });
      console.error('Error updating outreach contact:', error);
    },
  });
};

export const useCreateOutreachInteraction = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (interaction: Omit<OutreachInteraction, 'id' | 'created_at' | 'created_by'>) => {
      const { data, error } = await supabase
        .from('outreach_interactions')
        .insert({
          ...interaction,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-interactions'] });
      queryClient.invalidateQueries({ queryKey: ['outreach-contacts'] });
      toast({
        title: "Success",
        description: "Interaction logged successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to log interaction",
        variant: "destructive",
      });
      console.error('Error creating outreach interaction:', error);
    },
  });
};
